import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { LuMailbox } from "react-icons/lu";
import { FaPix } from "react-icons/fa6";

import "./UserProfile.css";
import toast from "react-hot-toast";

export function UserProfile() {
  const { user, userData, logout } = useAuth();
  const [meusPedidos, setMeusPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(true);
  const navigate = useNavigate();

  // Busca os pedidos do usuário em tempo real
  useEffect(() => {
    if (user) {
      const pedidosRef = collection(db, "pedidos");
      // Filtra pelo UID do usuário logado e ordena pelo mais recente
      const q = query(
        pedidosRef,
        where("userId", "==", user.uid),
        orderBy("dataCriacao", "desc"),
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const pedidosData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setMeusPedidos(pedidosData);
        setLoadingPedidos(false);
      });

      return () => unsubscribe();
    }
  }, [user]);

  // Função auxiliar para formatar a data do Firestore
  const formatarData = (timestamp) => {
    if (!timestamp) return "Data Indisponível";
    const data = timestamp.toDate();
    return data.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Função auxiliar para definir a cor e o texto do status
  const obterStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return { texto: "Aprovado", classe: "status-aprovado" };
      case "pending":
        return { texto: "Pendente", classe: "status-pendente" };
      case "in_process":
        return { texto: "Em Processo", classe: "status-processo" };
      case "rejected":
        return { texto: "Recusado", classe: "status-recusado" };
      case "cancelled":
        return { texto: "Cancelado", classe: "status-cancelado" };
      case "shipped":
        return { texto: "Enviado", classe: "status-enviado" };
      case "delivered":
        return { texto: "Entregue", classe: "status-entregue" };
      default:
        return { texto: status || "Desconhecido", classe: "status-padrao" };
    }
  };

  if (!user) return <div className="loading-profile">Carregando perfil...</div>;

  return (
    <div className="profile-page-wrapper">
      <div className="profile-container">
        <header className="profile-header">
          <motion.h1
            className="profile-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Minha Conta
          </motion.h1>
          <motion.p
            className="profile-subtitle"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Bem-vindo à sua área exclusiva na Repique Esporas.
          </motion.p>
        </header>

        <div className="profile-grid">
          {/* Coluna 1: Dados Pessoais e Endereço */}
          <aside className="profile-sidebar">
            <motion.div
              className="profile-card user-data-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="card-header">
                <h3>Dados Pessoais</h3>
              </div>
              <div className="card-body">
                <div className="info-group">
                  <label>NOME COMPLETO</label>
                  <span>{userData?.nome || "Não informado"}</span>
                </div>
                <div className="info-group">
                  <label>E-MAIL</label>
                  <span>{user.email}</span>
                </div>
                <button className="btn-logout-profile" onClick={handleLogout}>
                  Sair da conta
                </button>
              </div>
            </motion.div>

            {userData?.enderecoPadrão && (
              <motion.div
                className="profile-card address-card"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className="card-header">
                  <span className="icon">📍</span>
                  <h3>Endereço de Entrega</h3>
                </div>
                <div className="card-body">
                  <p>
                    {userData.enderecoPadrão.rua},{" "}
                    {userData.enderecoPadrão.numero}
                  </p>
                  <p>
                    {userData.enderecoPadrão.cidade} -{" "}
                    {userData.enderecoPadrão.estado}
                  </p>
                  <p>CEP: {userData.enderecoPadrão.cep}</p>
                </div>
              </motion.div>
            )}
          </aside>

          {/* Coluna 2: Lista de Pedidos */}
          <main className="profile-main">
            <motion.div
              className="profile-card orders-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="card-header">
                <h3>Meus Pedidos</h3>
              </div>

              <div className="card-body">
                {loadingPedidos ? (
                  <div className="loading-orders">Buscando seus pedidos...</div>
                ) : meusPedidos.length === 0 ? (
                  <div className="no-orders">
                    <span className="icon-empty">
                      <LuMailbox />
                    </span>
                    <p>Você ainda não realizou nenhum pedido.</p>
                    <button
                      className="btn-shop-now"
                      onClick={() => (window.location.href = "/produtos")}
                    >
                      VER PRODUTOS
                    </button>
                  </div>
                ) : (
                  <div className="orders-list">
                    {meusPedidos.map((pedido, index) => {
                      const statusInfo = obterStatusInfo(pedido.status);
                      return (
                        <motion.div
                          key={pedido.id}
                          className="order-item"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.5,
                            delay: index * 0.1,
                            ease: "easeOut",
                          }}
                        >
                          <div className="order-summary">
                            <div className="order-meta">
                              <span className="order-id">
                                Pedido: #{pedido.id.slice(-6).toUpperCase()}
                              </span>
                              <span className="order-date">
                                {formatarData(pedido.dataCriacao)}
                              </span>
                            </div>
                            <div className="order-status-price">
                              <span
                                className={`status-badge ${statusInfo.classe}`}
                              >
                                {statusInfo.texto}
                              </span>
                              <span className="order-total">
                                {pedido.total.toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </span>
                            </div>
                            {pedido.status?.toLowerCase() === "approved" && (
                              <div className="order-approved-message">
                                Sua compra foi aprovada! Aguarde, iremos enviar
                                o seu código de rastreio em breve. Qualquer
                                dúvida, entre em contato conosco.
                              </div>
                            )}
                          </div>

                          {/* Detalhes do Pedido (Itens comprados) */}
                          <div className="order-details">
                            <details>
                              <summary>
                                Ver itens do pedido ({pedido.itens?.length || 0}
                                )
                              </summary>
                              <div className="details-content">
                                {pedido.itens?.map((item, index) => (
                                  <div key={index} className="details-item-row">
                                    <div className="item-info">
                                      {/* Se tiver foto, mostre aqui */}
                                      {item.foto && (
                                        <img
                                          src={item.foto}
                                          alt={item.title}
                                          className="item-thumb"
                                        />
                                      )}
                                      <div className="item-text">
                                        <span className="item-title">
                                          {item.title}
                                        </span>
                                        <span className="item-qtd">
                                          Qtd: {item.quantity}
                                        </span>
                                      </div>
                                    </div>
                                    <span className="item-subtotal">
                                      {(
                                        item.unit_price * item.quantity
                                      ).toLocaleString("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                      })}
                                    </span>
                                  </div>
                                ))}
                                {/* Informações de Pagamento/Envio */}
                                <div className="details-footer">
                                  <p>
                                    <strong>Método:</strong>{" "}
                                    {pedido.paymentMethod?.toUpperCase()}
                                    {pedido.paymentMethod?.toLowerCase() ===
                                      "pix" && (
                                      <FaPix
                                        style={{
                                          marginLeft: "8px",
                                          color: "#32CD32",
                                        }}
                                      />
                                    )}
                                  </p>

                                  {pedido.status === "shipped" && (
                                    <div className="shipping-info-box">
                                      {pedido.tipoEnvio === "pessoal" ? (
                                        <p className="shipping-msg">
                                          <strong>Entrega em mãos:</strong>{" "}
                                          Nossa Equipe entregará seu pedido
                                          pessoalmente no seu endereço!
                                        </p>
                                      ) : (
                                        <div className="tracking-area">
                                          <p className="shipping-msg">
                                            <strong>A caminho:</strong> Seu
                                            pedido foi postado nos Correios.
                                          </p>
                                          <p>
                                            <strong>Código de Rastreio:</strong>{" "}
                                            <span className="tracking-code">
                                              {pedido.trackingNumber?.toUpperCase()}
                                            </span>
                                          </p>
                                          <a
                                            href={`https://www.linkcorreios.com.br/${pedido.trackingNumber}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn-track-external"
                                          >
                                            Rastrear no site dos Correios
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {pedido.status === "pending" &&
                                    pedido.paymentMethod === "pix" &&
                                    pedido.pixInfo && (
                                      <div className="pix-repayment-box">
                                        <p className="pix-alert">
                                          ⚠️ Pagamento ainda não identificado.
                                        </p>
                                        <div className="pix-repayment-content">
                                          <img
                                            src={`data:image/jpeg;base64,${pedido.pixInfo.qrCodeBase64}`}
                                            alt="QR Code Pix"
                                            className="pix-mini-qr"
                                          />
                                          <div className="pix-copy-area">
                                            <small>Código Copia e Cola:</small>
                                            <textarea
                                              readOnly
                                              value={pedido.pixInfo.qrCode}
                                              onClick={(e) => e.target.select()}
                                            />
                                            <button
                                              className="btn-copy-small"
                                              onClick={() => {
                                                navigator.clipboard.writeText(
                                                  pedido.pixInfo.qrCode,
                                                );
                                                toast.success(
                                                  "Código Pix copiado!",
                                                );
                                              }}
                                            >
                                              COPIAR CÓDIGO
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </details>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
}
